import React from 'react';
import cloneDeep from 'lodash.clonedeep';

import { hexToRGB } from '../../../../../utils/hex-to-rgb';
import { Timeline, useReels } from '../../../../../contexts/reels';

export const TextOverlay: React.FC<{
  title?: string;
  subtitle?: string;
  body?: string;
}> = ({ title, subtitle, body }) => {
  const { global_settings } = useReels();

  const content = React.useMemo(() => {
    const result: string[] = [];

    if (title) result.push(title);
    if (subtitle) result.push(subtitle);
    if (body) result.push(body);

    return result;
  }, [title, subtitle, body]);

  return (
    <>
      {global_settings.text_style.box_style === 'full_frame' && (
        <div
          style={{
            position: 'absolute',
            zIndex: 0,
            inset: 0,
            backgroundColor:
              global_settings.text_style.box_color.split('@')[0] ?? '',
            opacity: global_settings.text_style.box_color.split('@')[1] ?? 1
          }}
        />
      )}

      <div
        className={'d-flex flex-column gap-1'}
        style={{
          zIndex: 1,
          fontFamily: `"${global_settings.font}"`,
          color: global_settings.text_style.color,
          textAlign: global_settings.text_style.alignment
        }}>
        {content.map((text) => {
          if (!text || text === '') return;
          const boxStyle = () => {
            const style = global_settings.text_style.box_color.split('@');
            return { color: style[0] ?? '', opacity: style[1] ?? '' };
          };

          return (
            <div
              key={text}
              style={{
                position: 'relative',
                padding: 0,
                margin: 0,
                ...(global_settings.text_style.box_style === 'text_fit' && {
                  border: `${global_settings.text_style.box_border_width}px solid ${hexToRGB(
                    boxStyle().color
                  ).replace('1)', `${boxStyle().opacity})`)}`
                })
              }}>
              {global_settings.text_style.box_style === 'text_fit' && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 0,
                    inset: 0,
                    backgroundColor:
                      global_settings.text_style.box_color.split('@')[0] ?? '',
                    opacity:
                      global_settings.text_style.box_color.split('@')[1] ?? ''
                  }}
                />
              )}
              <div
                style={{
                  width: '100px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  position: 'relative'
                }}>
                {text}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export const TextItem: React.FC<{
  item: Timeline;
  isAdded?: boolean;
  displayDelete?: boolean;
}> = ({ item, isAdded, displayDelete }) => {
  const { textSlides, timeline, global_settings, handleChange } = useReels();

  const textContainerAlign = React.useMemo(() => {
    if (global_settings.text_style.alignment === 'left') return 'start';
    if (global_settings.text_style.alignment === 'center') return 'center';
    if (global_settings.text_style.alignment === 'right') return 'end';
    return 'center';
  }, [global_settings.text_style.alignment]);

  return (
    <div
      title={isAdded ? 'Remove from timeline' : 'Add to timeline'}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        position: 'relative',
        justifyContent: textContainerAlign,
        backgroundColor: item.background_color,
        ...(isAdded && {
          border: '3px solid #4698CB'
        })
      }}
      onClick={() => {
        if (isAdded) {
          const result = cloneDeep(timeline);
          const getId = result.findIndex((timeline) => timeline.id === item.id);
          if (getId !== -1) result.splice(getId, 1);
          handleChange({ timeline: result });
        } else {
          let newTimeline = cloneDeep(timeline);

          newTimeline = [...newTimeline, item];
          handleChange({ timeline: newTimeline });
        }
      }}>
      {isAdded && (
        <div
          className={
            'd-flex align-items-center justify-content-center p-1 position-absolute'
          }
          style={{
            top: '8px',
            zIndex: 99,
            left: '8px',
            color: 'white',
            fontSize: '12px',
            borderRadius: '4px',
            backgroundColor: '#4698CB'
          }}>
          Added
        </div>
      )}

      {displayDelete && (
        <div
          className={
            'd-flex align-items-center justify-content-center bg-white p-1 position-absolute'
          }
          style={{
            bottom: '8px',
            right: '8px',
            borderRadius: '4px',
            zIndex: 999
          }}
          title={'Remove from text slides'}
          onClick={() => {
            const textSlideId = textSlides.findIndex((ts) => ts.id === item.id);
            if (textSlideId !== -1) {
              const finalTextSlides = cloneDeep(textSlides);
              finalTextSlides.splice(textSlideId, 1);
              const timelineId = timeline.findIndex((t) => t.id === item.id);
              let finalTimeline: typeof timeline | undefined = undefined;
              if (timelineId !== -1) {
                finalTimeline = cloneDeep(timeline);
                finalTimeline.splice(timelineId, 1);
              }

              handleChange({
                textSlides: finalTextSlides,
                ...(finalTimeline !== undefined && {
                  timeline: finalTimeline
                })
              });
            }
          }}>
          <span
            className="material-symbols-outlined"
            style={{
              color: 'red',
              fontSize: '16px'
            }}>
            delete
          </span>
        </div>
      )}

      <TextOverlay
        title={item.content?.title}
        subtitle={item.content?.subtitle}
        body={item.content?.body}
      />
    </div>
  );
};

export const TextSlides: React.FC = () => {
  const { timeline, textSlides } = useReels();

  return (
    <div className={'text-slides'}>
      <div className={'d-flex gap-3 align-items-center'}>
        <b
          style={{
            fontSize: '18px'
          }}>
          Text Slides
        </b>
      </div>

      <div className={'reels-text-slides-grid-container'}>
        {textSlides
          .filter((t) => t.type === 'text')
          .map((t) => {
            const isAdded = timeline.find((e) => e.id === t.id);

            return (
              <div
                key={t.id}
                style={{
                  width: '100%',
                  height: '150px'
                }}>
                <TextItem
                  item={t}
                  isAdded={isAdded !== undefined}
                  displayDelete
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};
